import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, CircularProgress, Grid, IconButton, Paper, Typography } from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import ModalComponent from 'ui-components/ModalComponent';
import { Primary, Secondary } from 'ui-components/Buttons';
import { regionName } from './regions';

// Paths that decide what a customer is charged, as opposed to what they read.
const isBillingPath = (path) => /\.(pricing|razorpay_config)\b/.test(path);

// What each field is called in the interface. Without this the dialog reports
// the config's own path — "Plus · visible" — which names the key that changed
// rather than the change, at the one moment someone is deciding whether to put
// it in front of customers.
const FIELD_LABELS = {
    title: 'title',
    subtitle: 'subtitle',
    buttonText: 'button text',
    features: 'features',
    limits: 'usage limits',
    replaces: 'which plan it replaces',
};

// plans.plus_v2.pricing.IN.yearly -> "Plus · India yearly price"
const describe = (path, plans) => {
    const parts = path.split('.');
    if (parts[0] === 'plans') {
        const plan = plans?.[parts[1]]?.title || parts[1];
        const [, , field, region, period] = parts;

        if (field === 'pricing') {
            return `${plan} · ${regionName(region)} ${period || ''} price`.replace(/\s+/g, ' ').trim();
        }
        if (field === 'razorpay_config') {
            return `${plan} · ${regionName(region)} ${period || ''} Razorpay plan`.replace(/\s+/g, ' ').trim();
        }
        // Where the new value is a yes or no, say which way it went. "shown or
        // hidden on the pricing page" names the setting; "taken off the pricing
        // page" is the thing someone is about to do to customers.
        const planDraft = plans?.[parts[1]];
        if (field === 'visible') {
            return `${plan} · ${planDraft?.visible === false ? 'taken off the pricing page' : 'put on the pricing page'}`;
        }
        if (field === 'enabled') {
            return `${plan} · ${planDraft?.enabled === false ? 'switched off' : 'switched back on'}`;
        }
        if (field === 'popular') {
            return `${plan} · "Most popular" badge ${planDraft?.popular ? 'added' : 'removed'}`;
        }

        if (FIELD_LABELS[field]) {
            return `${plan} · ${FIELD_LABELS[field]}`;
        }

        const rest = parts.slice(2).join(' ').replace(/\[(\d+)\]/g, (_, i) => ` ${Number(i) + 1}`);
        return `${plan} · ${rest || 'details'}`;
    }
    if (parts[0] === 'comparison') {
        return `Comparison table · ${parts.slice(1).join(' ').replace(/\[(\d+)\]/g, (_, i) => ` ${Number(i) + 1}`)}`;
    }
    return path;
};

/**
 * What is about to go live, before it goes live.
 *
 * A save here publishes to a public pricing page with no draft step, so the
 * last thing between an edit and a customer seeing it is this list. Billing
 * changes are called out separately because they are the ones that can cost
 * money rather than just read badly.
 */
const PublishDialog = ({ open, handleClose, changes = [], plans, saving, onConfirm }) => {
    const theme = useTheme();
    if (!open) return null;

    const billing = changes.filter(isBillingPath);

    const Group = ({ title, paths, tone }) => {
        if (!paths.length) return null;
        const danger = tone === 'danger';
        return (
            <Grid item xs={12} sx={{
                padding: '0.875rem 1rem',
                borderRadius: '8px',
                backgroundColor: danger ? theme.palette.customColors.r50 : theme.palette.customColors.yellow_bg,
                border: `1px solid ${danger ? theme.palette.customColors.r75 : theme.palette.customColors.b50}`,
            }}>
                <Box display="flex" alignItems="center" gap={0.75} marginBottom={0.5}>
                    {danger && <WarningAmberRounded fontSize="small" sx={{ color: theme.palette.customColors.r400 }} />}
                    <Typography sx={{ fontWeight: 700, color: danger ? theme.palette.customColors.r500 : theme.palette.customColors.g300 }}>
                        {title}
                    </Typography>
                </Box>
                {paths.slice(0, 12).map((path) => (
                    <Typography key={path} sx={{
                        fontSize: '0.8125rem',
                        color: danger ? theme.palette.customColors.r400 : theme.palette.customColors.g200,
                    }}>
                        {describe(path, plans)}
                    </Typography>
                ))}
                {paths.length > 12 && (
                    <Typography sx={{ fontSize: '0.8125rem', color: theme.palette.customColors.g100, marginTop: 0.5 }}>
                        and {paths.length - 12} more
                    </Typography>
                )}
            </Grid>
        );
    };

    return (
        <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: 620, width: '100%' }}>
            <Paper sx={{ background: theme.palette.customColors.whiteBG, borderRadius: '8px' }}>
                <IconButton sx={{ position: 'absolute', top: 20, right: 20, padding: 0 }} onClick={handleClose}>
                    <CloseIcon width={40} height={40} />
                </IconButton>

                <Grid container display="grid" padding={'1.5rem'} gap={'1rem'}>
                    {/* The close button is absolutely positioned over this corner,
                        so the heading and its line keep clear of it rather than
                        running underneath. */}
                    <Grid item xs={12} sx={{ paddingRight: '3rem' }}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: theme.palette.customColors.g300 }}>
                            Publish {changes.length} change{changes.length === 1 ? '' : 's'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200, marginTop: 0.5 }}>
                            This goes live on the pricing pages on their next load. The version you are replacing is
                            kept, so you can put it back from History.
                        </Typography>
                    </Grid>

                    {/* Only prices and razorpay ids are listed. Everything else is
                        copy, which the editor behind this dialog is already showing;
                        repeating it here turned a confirmation into a second screen
                        to read. These are the changes that cost money, so they stay. */}
                    <Group title="Prices and payment settings" paths={billing} tone="danger" />

                    <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1.5}>
                        <Secondary onClick={handleClose} disabled={saving} sx={{ padding: '0.625rem 1.5rem' }}>
                            Keep editing
                        </Secondary>
                        <Primary onClick={onConfirm} disabled={saving} sx={{ padding: '0.625rem 1.5rem', minWidth: '9rem' }}>
                            {saving ? <CircularProgress sx={{ color: 'white' }} size={'1.2rem'} /> : 'Publish'}
                        </Primary>
                    </Grid>
                </Grid>
            </Paper>
        </ModalComponent>
    );
};

export default PublishDialog;
